import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import SourceCitations from './SourceCitations';
import GandalfLogo from '../common/GandalfLogo';

const ChatMessage = ({ message, sourceRefs }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Helper to scroll to source
  const scrollToSource = (sourceId) => {
    if (!sourceRefs?.current) return;
    const ref = sourceRefs.current[sourceId];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ref.classList.add('ring-2', 'ring-primary');
      setTimeout(() => ref.classList.remove('ring-2', 'ring-primary'), 1200);
    }
  };

  // Component for smooth streaming like ChatGPT/Perplexity
  const ProcessedMarkdown = ({ content }) => {
    const containerRef = useRef(null);

    // Process any text content to replace citations with React components
    const processTextForCitations = (text) => {
      if (message.isStreaming || !message.sources || typeof text !== 'string') {
        return text;
      }

      // Split content by [Source N] patterns and create React elements
      const parts = [];
      const regex = /\[Source (\d+)\]/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        // Add text before the citation
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }

        // Add citation component
        const sourceNum = parseInt(match[1], 10);
        const source = message.sources?.find(s => s.id === sourceNum);
        
        parts.push({
          type: 'citation',
          sourceNum,
          source,
          key: `citation-${sourceNum}-${match.index}`
        });

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      return parts.length > 1 ? parts : text;
    };

    // Process children recursively to handle any nested structure
    const processChildren = (children) => {
      if (typeof children === 'string') {
        return processTextForCitations(children);
      }
      
      if (Array.isArray(children)) {
        return children.map((child, index) => {
          if (typeof child === 'string') {
            return processTextForCitations(child);
          }
          return child;
        }).flat();
      }
      
      return children;
    };

    // Render processed content with citations
    const renderWithCitations = (content) => {
      if (Array.isArray(content)) {
        return content.map((part, index) => {
          if (typeof part === 'string') {
            return part;
          } else if (part?.type === 'citation') {
            return (
              <CitationButton
                key={part.key}
                sourceNum={part.sourceNum}
                source={part.source}
              />
            );
          }
          return part;
        });
      }
      return content;
    };

    // Custom citation component
    const CitationButton = ({ sourceNum, source }) => {
      const handleClick = () => {
        if (source?.url) {
          window.open(source.url, '_blank', 'noopener,noreferrer');
        } else {
          scrollToSource(sourceNum);
        }
      };

      // Determine source type and styling
      const isOpenverse = source?.url && (
        source.url.includes('openverse.fyi') || 
        source.url.includes('openverse.org')
      ) || source?.content_source === 'openverse_api' || source?.type === 'openverse_resource';
      
      const isOpenOrgResource = source?.type === 'openorg_resource';
      
      const isNewsletter = source?.type === 'newsletter' || 
        (source?.metadata && source.metadata.category === 'newsletter') ||
        (source?.url && (source.url.includes('substack.com') || 
                        source.url.includes('beehiiv.com') || 
                        source.url.includes('newsletter')));
      
      let displayType, badgeClassName;
      
      if (isOpenverse) {
        displayType = 'Openverse';
        badgeClassName = 'text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-indigo-500/20 text-purple-300 font-medium border border-purple-400/30';
      } else if (isOpenOrgResource) {
        displayType = 'Open Org';
        badgeClassName = 'text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-300 font-medium border border-orange-400/30';
      } else if (isNewsletter) {
        displayType = 'Newsletter';
        badgeClassName = 'text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 font-medium border border-yellow-400/30';
      } else {
        displayType = source?.type ? source.type.charAt(0).toUpperCase() + source.type.slice(1) : 'Source';
        badgeClassName = 'text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary-light font-medium';
      }

      const sourceTitle = source?.title || `Source ${sourceNum}`;
      const truncatedTitle = sourceTitle.length > 25 ? sourceTitle.substring(0, 25) + '...' : sourceTitle;

      return (
        <button
          onClick={handleClick}
          className="inline-flex items-center gap-2 text-primary hover:text-primary-light transition-colors duration-200 text-sm font-medium bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-md mx-1 cursor-pointer border border-primary/20 hover:border-primary/40"
          title={source ? `${source.title}${source.url ? ' (click to open)' : ' (click to view)'}` : `Source ${sourceNum}`}
        >
          <div className="flex items-center gap-2">
            {(isOpenverse || isNewsletter || source?.type) && (
              <span className={badgeClassName}>
                {displayType}
              </span>
            )}
            <span className="text-xs font-medium text-gray-300 max-w-32 truncate">
              {truncatedTitle}
            </span>
            {source?.url && (
              <svg className="w-3 h-3 text-primary-light flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            )}
          </div>
        </button>
      );
    };

    // Enhanced component factory that processes citations in any text element
    const createComponentWithCitations = (BaseComponent, className) => {
      return ({ children, ...props }) => {
        const processedChildren = processChildren(children);
        const renderedContent = renderWithCitations(processedChildren);
        
        return (
          <BaseComponent className={className} {...props}>
            {renderedContent}
          </BaseComponent>
        );
      };
    };

    // During streaming: use simple, lightweight components
    const StreamingComponents = {
      h1: ({ children }) => <h1 className="text-2xl font-bold text-white mb-4 mt-6 first:mt-0">{children}</h1>,
      h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-100 mb-3 mt-5 first:mt-0">{children}</h2>,
      h3: ({ children }) => <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4 first:mt-0">{children}</h3>,
      p: ({ children }) => <p className="text-gray-200 mb-4 last:mb-0 leading-relaxed">{children}</p>,
      ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-200">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-gray-200">{children}</ol>,
      li: ({ children }) => <li className="text-gray-200">{children}</li>,
      code: ({ inline, children }) => inline ? 
        <code className="bg-gray-700/50 text-primary-light px-1 py-0.5 rounded text-sm">{children}</code> :
        <pre className="bg-gray-900/80 text-gray-100 p-3 rounded-lg overflow-x-auto mb-4"><code>{children}</code></pre>,
      strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
      em: ({ children }) => <em className="italic text-gray-300">{children}</em>,
    };

    // After streaming: use full-featured components with citation processing
    const FullComponents = {
      h1: createComponentWithCitations('h1', 'text-2xl font-bold text-white mb-4 mt-6 first:mt-0 leading-tight'),
      h2: createComponentWithCitations('h2', 'text-xl font-semibold text-gray-100 mb-3 mt-5 first:mt-0 leading-tight'),
      h3: createComponentWithCitations('h3', 'text-lg font-medium text-gray-200 mb-2 mt-4 first:mt-0 leading-tight'),
      p: createComponentWithCitations('p', 'text-gray-200 mb-4 last:mb-0 leading-relaxed text-[15px]'),
      ul: createComponentWithCitations('ul', 'list-disc pl-6 mb-4 space-y-2 text-gray-200'),
      ol: createComponentWithCitations('ol', 'list-decimal pl-6 mb-4 space-y-2 text-gray-200'),
      li: createComponentWithCitations('li', 'text-gray-200 leading-relaxed text-[15px]'),
      blockquote: createComponentWithCitations('blockquote', 'border-l-4 border-primary/50 pl-4 py-2 bg-gray-700/30 rounded-r-lg mb-4 italic text-gray-300'),
      strong: createComponentWithCitations('strong', 'font-semibold text-white'),
      em: createComponentWithCitations('em', 'italic text-gray-300'),
      a: ({ href, children }) => (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-primary hover:text-primary-light underline transition-colors"
        >
          {children}
        </a>
      ),
      code: ({ inline, className, children }) => {
        if (inline) {
          return (
            <code className="bg-gray-700/50 text-primary-light px-2 py-1 rounded-md text-sm font-mono">
              {children}
            </code>
          );
        }
        return (
          <pre className="bg-gray-900/80 border border-gray-700/50 text-gray-100 p-4 rounded-xl overflow-x-auto backdrop-blur-sm mb-4">
            <code className={className}>{children}</code>
          </pre>
        );
      },
      table: ({ children }) => (
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full border-collapse border border-gray-600">
            {children}
          </table>
        </div>
      ),
      th: ({ children }) => (
        <th className="border border-gray-600 bg-gray-700/50 px-4 py-2 text-left font-semibold text-white">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="border border-gray-600 px-4 py-2 text-gray-200">
          {children}
        </td>
      ),
    };

    return (
      <div ref={containerRef}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={message.isStreaming ? [] : [rehypeHighlight]} // Skip heavy processing during streaming
          className={`prose max-w-none ${
            message.isError ? 'prose-red prose-invert' : 'prose-invert prose-primary'
          }`}
          components={message.isStreaming ? StreamingComponents : FullComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  if (message.role === 'user') {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className="flex justify-end"
      >
        <div className="max-w-3xl">
          <div className="bg-gradient-to-r from-primary to-purple-500 text-white rounded-2xl rounded-tr-md px-6 py-4 shadow-lg">
            <p className="whitespace-pre-wrap font-medium">{message.content}</p>
          </div>
          <div className="flex justify-end mt-2">
            <span className="text-xs text-gray-500">
              {formatTimestamp(message.timestamp)}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex justify-start"
    >
      <div className="max-w-4xl w-full">
        <div className={`rounded-2xl rounded-tl-md px-6 py-5 backdrop-blur-sm border shadow-lg ${
          message.isError 
            ? 'bg-red-900/20 border-red-500/30 text-red-100' 
            : 'bg-gray-800/50 border-gray-700/50 text-white'
        }`}>
          <div className="flex items-start space-x-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              message.isError 
                ? 'bg-red-500' 
                : 'bg-gradient-to-r from-primary to-purple-500'
            }`}>
              <GandalfLogo size="small" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <ProcessedMarkdown content={message.content} />
              
              {/* Typing indicator for streaming messages */}
              {message.isStreaming && (
                <div className="flex items-center mt-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2">Gandalf is typing...</span>
                </div>
              )}
            </div>
          </div>

          {/* Message Actions */}
          {!message.isStreaming && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-700/30">
              <span className="text-xs text-gray-500">
                {formatTimestamp(message.timestamp)}
              </span>
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={copyToClipboard}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50"
                  title="Copy message"
                >
                  {copied ? (
                    <CheckIcon className="w-4 h-4 text-green-400" />
                  ) : (
                    <ClipboardIcon className="w-4 h-4" />
                  )}
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage; 